// DJSEM Prompt 116 — DSP Asset AI Builder.
//
// Purpose: generate release/campaign assets for Spotify, SoundCloud, Apple Music, YouTube, TikTok,
// Instagram, EPK, etc. Hard rules from the spec:
//   • All AI generation MUST route through the Prompt 66 AI Provider Registry (frontend never
//     calls a provider directly — this module only produces a plan + estimate).
//   • All assets MUST go through the Prompt 65 Rights Ledger (nothing marked release-ready without
//     provenance).
//   • Nothing auto-approved. Assets are "pending review" until a user acts.
//   • Platform specs are configurable, not hardcoded assumptions.
//   • No copyrighted / style-infringing asset generation.

// ---- targets + spec registry --------------------------------------------------------------------

/** A platform/surface a DSP (release/campaign) asset is built for. */
export type DJSEMDSPAssetTarget =
  | "spotify_release" | "spotify_playlist" | "spotify_profile" | "spotify_canvas"
  | "soundcloud_track" | "soundcloud_playlist" | "soundcloud_profile"
  | "apple_music"
  | "youtube_thumbnail" | "youtube_visualizer" | "youtube_banner" | "youtube_shorts"
  | "tiktok_clip" | "tiktok_profile"
  | "instagram_post" | "instagram_story" | "instagram_profile" | "instagram_reel"
  | "facebook_post" | "facebook_event"
  | "x_profile"
  | "artist_profile"
  | "gig_poster" | "fan_drop"
  | "epk" | "press" | "public_page" | "distrokid_manual_pack" | "custom";

/** A configurable dimension/format spec for a single DSP asset target. */
export interface DJSEMDSPAssetSpec {
  id: string;
  target: DJSEMDSPAssetTarget;
  label: string;
  mediaType: "image" | "video" | "text" | "zip";
  width?: number;
  height?: number;
  aspectRatio?: string;
  durationMs?: number;
  fileFormat?: string;
  required: boolean;
  configurable: boolean;
  warnings: string[];
}

/**
 * The default spec registry. All numeric dimensions are current-best-effort defaults and are marked
 * `configurable: true` so integrators can override them without a code change (the spec explicitly
 * requires this — platform requirements change).
 */
export function defaultDSPAssetSpecs(): DJSEMDSPAssetSpec[] {
  const spec = (
    id: string, target: DJSEMDSPAssetTarget, label: string,
    o: Partial<DJSEMDSPAssetSpec> = {},
  ): DJSEMDSPAssetSpec => ({
    id, target, label, mediaType: o.mediaType ?? "image", required: false, configurable: true, warnings: [], ...o,
  });
  return [
    spec("spotify_cover_square",   "spotify_release",   "Spotify release cover",           { width: 3000, height: 3000, aspectRatio: "1:1", required: true, fileFormat: "jpg" }),
    spec("spotify_playlist_cover", "spotify_playlist",  "Spotify playlist cover",          { width: 1500, height: 1500, aspectRatio: "1:1" }),
    spec("soundcloud_artwork",     "soundcloud_track",  "SoundCloud track artwork",        { width: 1400, height: 1400, aspectRatio: "1:1" }),
    spec("soundcloud_banner",      "soundcloud_track",  "SoundCloud banner",               { width: 2480, height: 520, aspectRatio: "31:6.5" }),
    spec("apple_music_cover",      "apple_music",       "Apple Music release cover",       { width: 3000, height: 3000, aspectRatio: "1:1", required: true }),
    spec("youtube_thumb",          "youtube_thumbnail", "YouTube thumbnail",               { width: 1280, height: 720,  aspectRatio: "16:9" }),
    spec("youtube_visualizer",     "youtube_visualizer","YouTube visualizer background",   { width: 1920, height: 1080, aspectRatio: "16:9", mediaType: "video", durationMs: 60000 }),
    spec("tiktok_clip",            "tiktok_clip",       "TikTok teaser clip",              { width: 1080, height: 1920, aspectRatio: "9:16", mediaType: "video", durationMs: 15000 }),
    spec("instagram_square",       "instagram_post",    "Instagram square post",           { width: 1080, height: 1080, aspectRatio: "1:1" }),
    spec("instagram_story",        "instagram_story",   "Instagram story",                 { width: 1080, height: 1920, aspectRatio: "9:16" }),
    spec("facebook_share",         "facebook_post",     "Facebook share",                  { width: 1200, height: 630,  aspectRatio: "1.91:1" }),
    spec("epk_hero",               "epk",               "EPK hero image",                  { width: 2400, height: 1350, aspectRatio: "16:9" }),
    spec("press_photo",            "press",             "Press photo variant",             { width: 3000, height: 2000, aspectRatio: "3:2" }),
    spec("distrokid_pack_zip",     "distrokid_manual_pack", "DistroKid manual pack ZIP",   { mediaType: "zip", fileFormat: "zip" }),
    // ---- V3 Artist IDE guardrail (Pillar 5) — artist profile + social presets ----
    // Dimensions are current-best-effort defaults; all `configurable: true` so integrators can
    // override without code changes. Safe-area notes go in `warnings` where platforms crop.
    spec("artist_profile_square",  "artist_profile",    "Artist profile picture (universal square)", { width: 1000, height: 1000, aspectRatio: "1:1" }),
    spec("soundcloud_profile",     "soundcloud_profile","SoundCloud profile image",        { width: 1000, height: 1000, aspectRatio: "1:1" }),
    spec("spotify_profile",        "spotify_profile",   "Spotify artist profile image",    { width: 750,  height: 750,  aspectRatio: "1:1", warnings: ["Spotify for Artists crops to a circle — keep the subject centred."] }),
    spec("spotify_canvas_loop",    "spotify_canvas",    "Spotify Canvas-style loop",       { width: 1080, height: 1920, aspectRatio: "9:16", mediaType: "video", durationMs: 8000, warnings: ["Canvas loops are 3–8 s, no audio."] }),
    spec("youtube_banner",         "youtube_banner",    "YouTube channel banner",          { width: 2560, height: 1440, aspectRatio: "16:9", warnings: ["Safe area 1546×423 centred — TV crops the rest."] }),
    spec("youtube_shorts_cover",   "youtube_shorts",    "YouTube Shorts cover",            { width: 1080, height: 1920, aspectRatio: "9:16" }),
    spec("tiktok_profile",         "tiktok_profile",    "TikTok profile image",            { width: 200,  height: 200,  aspectRatio: "1:1", warnings: ["Rendered small — keep it bold and simple."] }),
    spec("instagram_profile",      "instagram_profile", "Instagram profile image",         { width: 320,  height: 320,  aspectRatio: "1:1", warnings: ["Cropped to a circle."] }),
    spec("instagram_reel_cover",   "instagram_reel",    "Instagram reel cover",            { width: 1080, height: 1920, aspectRatio: "9:16" }),
    spec("facebook_event_banner",  "facebook_event",    "Facebook event banner",           { width: 1920, height: 1005, aspectRatio: "1.91:1" }),
    spec("x_banner",               "x_profile",         "X / Twitter header banner",       { width: 1500, height: 500,  aspectRatio: "3:1", warnings: ["Profile photo overlaps bottom-left — keep it clear."] }),
    spec("public_page_hero",       "public_page",       "Public page hero",                { width: 2400, height: 1200, aspectRatio: "2:1" }),
    spec("gig_poster_portrait",    "gig_poster",        "Gig / tour poster (portrait)",    { width: 1414, height: 2000, aspectRatio: "A-series" }),
    spec("fan_drop_artwork",       "fan_drop",          "Fan drop / competition artwork",  { width: 1600, height: 1600, aspectRatio: "1:1" }),
  ];
}

/** Filter default specs by requested targets. */
export function listSpecsForTargets(targets: DJSEMDSPAssetTarget[]): DJSEMDSPAssetSpec[] {
  const set = new Set(targets);
  return defaultDSPAssetSpecs().filter((s) => set.has(s.target));
}

// ---- generation plan + credit estimate + result -------------------------------------------------

/** A request to generate DSP assets for a set of platform targets. */
export interface DJSEMDSPAssetGenerationRequest {
  id: string;
  organisationId?: string;
  projectId?: string;
  releaseId?: string;
  trackId?: string;
  brandWorldId?: string;
  targets: DJSEMDSPAssetTarget[];
  sourceAssetIds: string[];        // approved source assets (e.g. cover art) to derive variants from
  prompt?: string;                 // creator's intent (rewritten by AI prompt builder downstream)
  qualityMode: "preview" | "standard" | "high";
  estimatedCredits?: number;
  userConfirmedCost: boolean;
  createdBy?: string;
  createdAt: string;
}

/** A generation plan for a DSP asset request: resolved specs, per-spec/total credit cost, and routing guarantees. */
export interface DJSEMDSPAssetGenerationPlan {
  id: string;
  requestId: string;
  specs: DJSEMDSPAssetSpec[];
  perSpecCredits: Array<{ specId: string; credits: number }>;
  totalCredits: number;
  usesAIProviderRegistry: true;    // never call a provider directly from frontend
  usesRightsLedger: true;
  warnings: string[];
  createdAt: string;
}

/** The result of executing a DSP asset generation plan. */
export interface DJSEMDSPAssetGenerationResult {
  id: string;
  requestId: string;
  generatedAssetIds: string[];
  brandConsistencyScores: Record<string, number>;
  rightsDeclarationIds: string[];
  warnings: string[];
  createdAt: string;
}

/** Credit cost per spec, scaled by qualityMode. Deterministic + tunable. */
export function estimateSpecCredits(spec: DJSEMDSPAssetSpec, qualityMode: "preview" | "standard" | "high"): number {
  const baseByMedia: Record<DJSEMDSPAssetSpec["mediaType"], number> = { image: 4, video: 20, text: 1, zip: 0 };
  const qMul = qualityMode === "preview" ? 0.5 : qualityMode === "standard" ? 1 : 2;
  return Math.max(0, Math.round(baseByMedia[spec.mediaType] * qMul));
}

/**
 * Build a generation plan for a request. Returns per-spec credit costs and the total; the plan
 * asserts routing through the Provider Registry + Rights Ledger. UI should not offer "generate"
 * until `userConfirmedCost` is true on the request.
 */
export function createDSPAssetGenerationPlan(req: DJSEMDSPAssetGenerationRequest): DJSEMDSPAssetGenerationPlan {
  const specs = listSpecsForTargets(req.targets);
  const perSpecCredits = specs.map((s) => ({ specId: s.id, credits: estimateSpecCredits(s, req.qualityMode) }));
  const totalCredits = perSpecCredits.reduce((sum, x) => sum + x.credits, 0);
  const warnings: string[] = [];
  if (!req.userConfirmedCost) warnings.push("user has not confirmed credit estimate — generation must not start yet");
  if (req.sourceAssetIds.length === 0) warnings.push("no approved source asset provided — variants must derive from an approved source");
  if (!req.brandWorldId) warnings.push("no Brand World selected — brand consistency scoring will be skipped");
  return {
    id: `dspplan_${req.id}`,
    requestId: req.id,
    specs, perSpecCredits, totalCredits,
    usesAIProviderRegistry: true, usesRightsLedger: true,
    warnings,
    createdAt: req.createdAt,
  };
}

// ---- brand consistency (deterministic scorer, expects downstream AI signal) ---------------------

/** Input for {@link brandConsistencyScore}: three 0..1 similarity signals from an upstream AI check. */
export interface BrandConsistencyInputs {
  primaryColorMatch: number;  // 0..1 similarity of dominant palette to Brand World
  typographyMatch: number;    // 0..1
  moodMatch: number;          // 0..1
}

/** Simple weighted mean; upstream AI produces the three inputs. */
export function brandConsistencyScore(i: BrandConsistencyInputs): number {
  const s = i.primaryColorMatch * 0.4 + i.typographyMatch * 0.2 + i.moodMatch * 0.4;
  return Math.max(0, Math.min(1, Number(s.toFixed(3))));
}

/** Low brand score = must be reviewed before use. */
export function requiresReviewForBrand(score: number): boolean { return score < 0.6; }

// ---- validation --------------------------------------------------------------------------------

/**
 * Validate a generated asset's dimensions/media against its spec. Non-throwing — returns issues
 * so the UI can display them. Zero dimensions on a `configurable` spec are treated as unknown
 * (skip check), matching the "specs are configurable" rule.
 */
export function validateAssetAgainstSpec(spec: DJSEMDSPAssetSpec, actual: { width?: number; height?: number; mediaType?: string; durationMs?: number }): { ok: boolean; issues: string[] } {
  const issues: string[] = [];
  if (spec.mediaType && actual.mediaType && actual.mediaType !== spec.mediaType) issues.push(`mediaType ${actual.mediaType} != ${spec.mediaType}`);
  if (spec.width && actual.width && actual.width !== spec.width) issues.push(`width ${actual.width} != ${spec.width}`);
  if (spec.height && actual.height && actual.height !== spec.height) issues.push(`height ${actual.height} != ${spec.height}`);
  if (spec.durationMs && actual.durationMs && Math.abs(actual.durationMs - spec.durationMs) > spec.durationMs * 0.1) {
    issues.push(`durationMs ${actual.durationMs} outside ±10% of ${spec.durationMs}`);
  }
  return { ok: issues.length === 0, issues };
}

// ---- asset pack ---------------------------------------------------------------------------------

/** A finished pack of approved DSP assets, grouped by target platform. */
export interface DJSEMDSPAssetPack {
  id: string;
  releaseId?: string;
  byPlatform: Record<string, string[]>;   // target → assetIds
  manifestRef: string;
  warnings: string[];
  createdAt: string;
}

/**
 * Group a release's approved assets into a pack keyed by target platform.
 * @param input - the release id, the approved assets to include, and an optional timestamp.
 * @returns the constructed asset pack (with a warning if no approved assets were given).
 */
export function createAssetPack(input: {
  releaseId?: string;
  approvedAssets: Array<{ id: string; target: DJSEMDSPAssetTarget }>;
  now?: string;
}): DJSEMDSPAssetPack {
  const now = input.now ?? new Date().toISOString();
  const byPlatform: Record<string, string[]> = {};
  for (const a of input.approvedAssets) {
    if (!byPlatform[a.target]) byPlatform[a.target] = [];
    byPlatform[a.target].push(a.id);
  }
  const warnings: string[] = [];
  if (input.approvedAssets.length === 0) warnings.push("no approved assets to include in pack");
  return {
    id: `dspack_${input.releaseId ?? "unrel"}_${Date.parse(now) || 0}`,
    releaseId: input.releaseId,
    byPlatform,
    manifestRef: `manifest:dsp:${input.releaseId ?? "unrel"}`,
    warnings,
    createdAt: now,
  };
}
